/**
 * combo-blackout.js — "leve a cortina de trás" na PDP do linho sem forro.
 *
 * Duas metades, no mesmo padrão de assets/ambiente-tag.js e assets/whatsapp-cta.js:
 *  1. Funções puras (testadas em tests/combo-blackout.test.js via `node --test`)
 *  2. O custom element <combo-blackout>, que só roda no navegador (guardado por `typeof document`)
 *
 * FORMA: cada opção é um gatilho de texto ("+ adicionar blackout 100% (separado)") que abre
 * o bloco com foto, pílula, frase e cor. Abrir NÃO adiciona: o item entra quando a cor é
 * escolhida, e fechar a linha tira. São até três linhas independentes — dá para levar
 * blackout 100% e microfibra na mesma compra.
 *
 * COMO OS ITENS EXTRAS ENTRAM NO CARRINHO
 * Teste contra a loja (18/08): `id` e `items[]` num mesmo POST /cart/add são processados
 * os DOIS e SOMAM — mandar id=linho + items[0]=linho + items[1]=blackout pôs o linho
 * duas vezes no carrinho. Por isso a divisão aqui é:
 *    id / quantity        → continua sendo o produto da página (o tema cuida)
 *    items[0..n][...]     → só as cortinas de trás
 * O linho PRECISA continuar em `id`: depois do add o tema faz
 * `const id = formData.get('id'); if (!id) throw` (assets/product-form.js:445) e, sem isso,
 * o produto entra no carrinho mas a gaveta nunca abre.
 * Os índices de `items[N]` são atribuídos aqui, densos a partir de 0, na ordem das linhas —
 * marcar só a terceira opção não pode virar `items[2]` sozinho no FormData.
 *
 * QUANDO OS CAMPOS SÃO PREENCHIDOS
 * O tema registra os handlers declarativos em `document` com { capture: true }
 * (assets/component.js:277), então um listener de submit no formulário chegaria DEPOIS
 * dele — tarde demais, o FormData já foi montado. A âncora aqui é o clique no botão de
 * comprar, que acontece antes de o evento de submit existir. A barra sticky não precisa de
 * tratamento: ela só dá `.click()` no botão real (assets/sticky-add-to-cart.js:168).
 */
(function (root) {
  'use strict';

  /** Propriedades que a janela compartilha: mesma altura, mesma abertura, mesmo cômodo. */
  var PROPS_HERDADAS = ['Altura desejada', 'Abertura', 'Ambiente'];

  /**
   * Propriedades que existem nos dois produtos com NOMES diferentes, no formato
   * 'De>Para,De>Para'. Quem decide é o Liquid (data-espelhos), porque depende do par:
   * no varão o linho pergunta 'Cor do Ilhós' e o blackout chama a mesma peça de
   * 'Cor da Argola' — sem espelhar, a produção recebe a peça sem saber qual usar.
   * No trilho a lista é vazia: não há argola.
   * @param {string} texto
   * @returns {Array<[string, string]>}
   */
  function espelhosDeTexto(texto) {
    var out = [];
    if (!texto) return out;
    texto.split(',').forEach(function (par) {
      var lados = par.split('>');
      if (lados.length !== 2) return;
      var de = lados[0].trim();
      var para = lados[1].trim();
      if (de && para) out.push([de, para]);
    });
    return out;
  }

  /**
   * Centímetros a partir do rótulo da variante ('2,25m' → 225).
   * Mesma conta do quiz (blocks/quiz-personalizacao.liquid:387), de propósito: os dois
   * precisam concordar sobre o que é "a mesma largura".
   */
  function cmDaLargura(label) {
    if (!label) return null;
    var n = parseFloat(String(label).replace(',', '.'));
    if (isNaN(n)) return null;
    return Math.round(n * 100);
  }

  /**
   * A régua de consumo de tecido vem de assets/regua-consumo.js (GERADO a partir de
   * precificacao/src/consumo.js — não editar). No navegador é o global ReguaConsumo (o
   * Liquid carrega o script antes deste); no Node, dos testes, vem por require.
   */
  var Regua = typeof ReguaConsumo !== 'undefined' ? ReguaConsumo : (typeof require === 'function' ? require('./regua-consumo.js') : null);

  /**
   * Metros de tecido consumidos, em número. O fator é o do produto de DESTINO — linho e
   * blackout de tecido franzem 2,0, então copiar o número do linho poria 4,5 m numa peça
   * de 2,45 m.
   *
   * Prega painel (blackout 100%) é o caso à parte: NÃO franze. Sai lisa, com 1x a largura
   * do trilho mais 20cm de folga no total — um trilho de 3,00m consome 3,20m, que com
   * abertura central viram duas partes de 1,60m.
   *
   * ATENÇÃO: na prega painel o `fator` é ignorado de propósito. O 1,2 que o blackout 100%
   * carrega é o multiplicador de PREÇO que gerou as variantes na Shopify
   * (configurador-promacal/backend/src/calculator.js), não metragem — é a mesma armadilha
   * do `v.fator` da API de medidas livres, que já pôs 10,6 m numa cortina de 15,0 m.
   */
  function metrosDeTecido(cm, fator, prega) {
    // `fator` fica na assinatura só por compatibilidade: quem manda é a prega (franzida 2x | painel 1x + 20cm).
    return Regua.metrosUteis(cm / 100, prega === 'painel' ? 'painel' : 'franzida');
  }

  /**
   * O mesmo consumo, formatado. O painel sai em duas casas porque a folga de 20cm cai fora
   * da grade de 25cm das variantes (2,25 + 0,20 = 2,45, que numa casa só mentiria 5cm); o
   * franzido segue em uma, onde múltiplo de 0,5 já é exato.
   */
  function consumoTecido(cm, fator, prega) {
    var casas = prega === 'painel' ? 2 : 1;
    return metrosDeTecido(cm, fator, prega).toFixed(casas).replace('.', ',');
  }

  /**
   * Texto da property "Largura em tecido". Precisa sair IDÊNTICO ao do quiz
   * (larguraEmTecido, blocks/quiz-personalizacao.liquid:611) porque é o que a planilha
   * de produção lê. Sem abertura escolhida não emite nada, em vez de emitir pela metade.
   */
  function larguraEmTecido(cm, fator, abertura, prega) {
    if (!cm || !abertura) return '';
    var total = metrosDeTecido(cm, fator, prega);
    var txt = consumoTecido(cm, fator, prega) + ' m';
    return abertura === 'Abertura central'
      ? txt + ' — duas partes de ' + (total / 2).toFixed(2).replace('.', ',') + ' m'
      : txt + ' — uma parte única';
  }

  /**
   * Properties do item extra, a partir das que o cliente já preencheu para o produto da
   * página. Herda o que é da janela, espelha o que muda de nome entre os dois produtos,
   * recalcula o tecido com o fator do destino e descarta o resto.
   * @param {Object|null} origem
   * @param {{cm: number|null, fator: number, prega?: string, espelhos?: Array<[string, string]>}} opcoes
   */
  function propriedadesDoPar(origem, opcoes) {
    var out = {};
    if (!origem) return out;
    for (var i = 0; i < PROPS_HERDADAS.length; i++) {
      var chave = PROPS_HERDADAS[i];
      var valor = origem[chave];
      if (valor) out[chave] = valor;
    }
    var espelhos = opcoes.espelhos || [];
    for (var j = 0; j < espelhos.length; j++) {
      var vindo = origem[espelhos[j][0]];
      if (vindo) out[espelhos[j][1]] = vindo;
    }
    var tecido = larguraEmTecido(opcoes.cm, opcoes.fator, origem['Abertura'] || '', opcoes.prega);
    if (tecido) out['Largura em tecido'] = tecido;
    return out;
  }

  /** Variante do destino pela largura herdada + a cor que o cliente escolheu na linha. */
  function varianteDoPar(mapa, largura, cor) {
    if (!mapa || !largura || !cor) return null;
    return mapa[largura + '|' + cor] || null;
  }

  /**
   * Largura a partir da variante escolhida no produto da página.
   * Lê do id da variante em vez do payload de `variant:update`: a JSDoc do evento
   * (assets/events.js:53) não garante `options` no resource, e depender disso deixava a
   * medida presa na largura inicial quando o cliente trocava.
   */
  function larguraDaVariante(mapa, varianteId) {
    if (!mapa || !varianteId) return '';
    return mapa[String(varianteId)] || '';
  }

  /**
   * Pares nome/valor de UM item extra, no formato que o /cart/add da Shopify espera.
   * O índice é do slot no FormData, não da linha na tela: com três linhas e só a última
   * escolhida, ela ocupa `items[0]` — índice furado não vira array do lado da Shopify.
   */
  function camposDoItem(varianteId, quantidade, props, indice) {
    if (!varianteId) return [];
    var n = indice || 0;
    var prefixo = 'items[' + n + ']';
    var campos = [
      [prefixo + '[id]', String(varianteId)],
      [prefixo + '[quantity]', String(quantidade || 1)],
    ];
    Object.keys(props || {}).forEach(function (chave) {
      campos.push([prefixo + '[properties][' + chave + ']', props[chave]]);
    });
    return campos;
  }

  /**
   * Campos de várias linhas de uma vez, já com os slots densos.
   * @param {Array<{varianteId: string|null, quantidade: number, props: Object}>} itens
   */
  function camposDosItens(itens) {
    var campos = [];
    var slot = 0;
    (itens || []).forEach(function (item) {
      if (!item || !item.varianteId) return;
      camposDoItem(item.varianteId, item.quantidade, item.props, slot).forEach(function (par) {
        campos.push(par);
      });
      slot += 1;
    });
    return campos;
  }

  /** Centavos → 'R$ 1.029,90'. */
  function formatarBRL(centavos) {
    var n = (Number(centavos) || 0) / 100;
    var inteiro = Math.floor(n);
    var cents = Math.round((n - inteiro) * 100);
    var milhar = String(inteiro).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return 'R$ ' + milhar + ',' + (cents < 10 ? '0' + cents : String(cents));
  }

  var NUMERAIS = { 2: 'duas', 3: 'três', 4: 'quatro', 5: 'cinco' };

  /**
   * Rótulo do botão de compra. `total` conta o produto da página + as cortinas de trás.
   * Com 1 volta a ser o texto do tema — que é o único jeito de não pisar em "Esgotado".
   */
  function rotuloBotao(total, padrao) {
    var n = Number(total) || 1;
    if (n <= 1) return padrao;
    return 'Adicionar as ' + (NUMERAIS[n] || String(n)) + ' ao carrinho';
  }

  /**
   * Texto da linha de promo no resumo (abaixo do subtotal). Mesmo epoch de
   * snippets/promo-leve2.liquid (fonte única do desconto no resto do site):
   * até seg 31/08/2026 23h59 (Brasília) = 1788231599, ainda é o Leve 2 (20% na
   * mais barata, BxGy). Depois disso o texto é o da oferta de outubro (20% na
   * 2ª, automático). Em novembro (a partir de dom 01/11/2026 00:00 = 1793502000) é o Black
   * November: desconto por quantidade de cortinas, automático. `agoraMs` é injetado (não `Date.now()`
   * direto) para o teste poder simular os dois lados do corte.
   */
  function textoPromo(agoraMs) {
    var fimLeve2Ms = 1788231599 * 1000;
    if ((Number(agoraMs) || 0) <= fimLeve2Ms) {
      return 'O desconto Leve 2 (20% na mais barata) entra no carrinho.';
    }
    if ((Number(agoraMs) || 0) < 1793502000 * 1000) {
      return 'Leve 2 cortinas e a segunda sai com 20% OFF — automático no carrinho.';
    }
    return 'Black November: quanto mais cortinas, maior o desconto — automático no carrinho.';
  }

  /**
   * Letra do teste A/B (snippets/ab-test.liquid grava o cookie `pmc_ab`). Vai em todo
   * evento para que o funil do combo possa ser lido por braço no GA4.
   */
  function variantAB(cookie) {
    var m = String(cookie == null ? '' : cookie).match(/pmc_ab=([ab])/);
    return m ? m[1] : undefined;
  }

  /**
   * Um evento do funil do combo. A janela é injetada em vez de lida do global para que
   * o teste possa passar um dataLayer de verdade em vez de mexer no ambiente.
   *
   * Três destinos, todos opcionais:
   *  - dataLayer  → GTM
   *  - gtag       → GA4 (mesmo caminho já verificado do quiz e do whatsapp-cta)
   *  - clarity    → etiqueta a GRAVAÇÃO da sessão, para filtrar "expandiu e não escolheu
   *                 cor" no painel e assistir. Só o último passo, que é o que interessa.
   *
   * Tudo dentro de try: bloqueador de anúncio, gtag meio carregado ou GTM ausente não
   * podem derrubar a escolha de cor — sem tracking o combo tem que continuar vendendo.
   */
  function enviarEvento(janela, nome, dados) {
    try {
      if (!janela) return;
      var limpo = {};
      Object.keys(dados || {}).forEach(function (chave) {
        var v = dados[chave];
        if (v !== undefined && v !== null && v !== '') limpo[chave] = v;
      });
      janela.dataLayer = janela.dataLayer || [];
      var comEvento = { event: nome };
      Object.keys(limpo).forEach(function (chave) {
        comEvento[chave] = limpo[chave];
      });
      janela.dataLayer.push(comEvento);
      if (typeof janela.gtag === 'function') janela.gtag('event', nome, limpo);
      if (typeof janela.clarity === 'function') janela.clarity('set', 'combo_blackout', nome);
    } catch (e) {}
  }

  var api = {
    PROPS_HERDADAS: PROPS_HERDADAS,
    espelhosDeTexto: espelhosDeTexto,
    cmDaLargura: cmDaLargura,
    metrosDeTecido: metrosDeTecido,
    consumoTecido: consumoTecido,
    larguraEmTecido: larguraEmTecido,
    propriedadesDoPar: propriedadesDoPar,
    varianteDoPar: varianteDoPar,
    larguraDaVariante: larguraDaVariante,
    camposDoItem: camposDoItem,
    camposDosItens: camposDosItens,
    formatarBRL: formatarBRL,
    rotuloBotao: rotuloBotao,
    textoPromo: textoPromo,
    variantAB: variantAB,
    enviarEvento: enviarEvento,
  };

  // ---------------------------------------------------------------------------------
  // Metade do navegador
  // ---------------------------------------------------------------------------------
  if (typeof document !== 'undefined' && typeof HTMLElement !== 'undefined') {
    function jsonSeguro(texto) {
      try {
        return JSON.parse(texto || '{}');
      } catch (e) {
        return {};
      }
    }

    class ComboBlackout extends HTMLElement {
      connectedCallback() {
        this.formEl = document.getElementById(this.dataset.formId);
        if (!this.formEl) return;

        // O tema re-renderiza a seção do produto a cada troca de variante e o elemento é
        // reinserido no DOM — connectedCallback roda de novo com os MESMOS nós filhos.
        // Sem esta guarda cada troca de variante dobrava os listeners: depois de o cliente
        // passar pelo quiz, um clique no gatilho virava dois eventos no GA4 (e duas
        // sincronizações). Quando o innerHTML é realmente trocado a primeira linha é outro
        // nó — aí religar é o certo, porque os listeners velhos morreram com os nós velhos.
        var primeira = this.querySelector('[data-cb-linha]');
        if (this._ligado && primeira === this._primeiraLinha) return;
        this._ligado = true;
        this._primeiraLinha = primeira;

        this.secao = this.closest('.shopify-section') || document;
        this.largurasOrigem = jsonSeguro(this.dataset.largurasOrigem);
        this.largura = this.dataset.larguraInicial || '';
        this.precoOrigem = parseInt(this.dataset.precoOrigem, 10) || 0;
        this.tituloOrigem = this.dataset.tituloOrigem || '';

        this.elResumo = this.querySelector('[data-cb-resumo]');
        this.elCampos = this.querySelector('[data-cb-campos]');

        this.linhas = Array.from(this.querySelectorAll('[data-cb-linha]')).map((el) => ({
          el: el,
          titulo: el.dataset.titulo || '',
          fator: parseFloat(el.dataset.fator) || 1.2,
          prega: el.dataset.prega || 'franzida',
          espelhos: espelhosDeTexto(el.dataset.espelhos || ''),
          mapa: jsonSeguro(el.dataset.variantes),
          // Chave do modelo nas Medidas Livres (ex.: 'bk100-avulso-trilho'): usada só no modo livre
          modeloLivre: el.dataset.modeloLivre || '',
          precos: jsonSeguro(el.dataset.precos),
          gt: el.querySelector('[data-cb-gt]'),
          sig: el.querySelector('[data-cb-sig]'),
          alvo: el.querySelector('[data-cb-alvo]'),
          chips: Array.from(el.querySelectorAll('[data-cb-cor]')),
          elMedida: el.querySelector('[data-cb-medida]'),
          elPreco: el.querySelector('[data-cb-preco]'),
          elGtPreco: el.querySelector('[data-cb-gt-preco]'),
          elAviso: el.querySelector('[data-cb-aviso]'),
          elRm: el.querySelector('[data-cb-rm]'),
          aberto: false,
          // Sem cor pré-escolhida de propósito: nos pedidos Branco e Marfim saem 11 a 9,
          // não existe padrão que valha um palpite.
          cor: '',
        }));

        if (!this.linhas.length) return;

        this.linhas.forEach((linha) => {
          if (linha.gt) {
            linha.gt.addEventListener('click', () => {
              if (linha.aberto) {
                // Fechar o gatilho tira o item: se já havia cor, isso é uma desistência
                // depois de escolher — o que separa "só espiou" de "tirou do carrinho".
                this.track('combo_remover', {
                  par_titulo: linha.titulo,
                  cor: linha.cor || undefined,
                  tinha_cor: linha.cor ? 'sim' : 'nao',
                  origem: 'gatilho',
                });
                this.fechar(linha);
              } else {
                linha.aberto = true;
                this.track('combo_expandir', { par_titulo: linha.titulo });
              }
              this.sincronizar();
            });
          }
          linha.chips.forEach((chip) => {
            chip.addEventListener('click', () => {
              var cor = chip.getAttribute('data-cb-cor') || '';
              // Escolher a cor é o que injeta o items[] — é este o evento que conta como
              // "adicionou", não o expandir. `trocou` separa quem mudou de ideia de cor
              // de quem escolheu pela primeira vez.
              this.track('combo_cor', {
                par_titulo: linha.titulo,
                cor: cor,
                trocou: linha.cor && linha.cor !== cor ? 'sim' : 'nao',
                preco: linha.precos[this.largura] ? linha.precos[this.largura] / 100 : undefined,
              });
              linha.cor = cor;
              linha.aberto = true;
              this.sincronizar();
            });
          });
          if (linha.elRm) {
            linha.elRm.addEventListener('click', () => {
              this.track('combo_remover', {
                par_titulo: linha.titulo,
                cor: linha.cor || undefined,
                tinha_cor: linha.cor ? 'sim' : 'nao',
                origem: 'botao',
              });
              this.fechar(linha);
              this.sincronizar();
            });
          }
        });

        // Daqui para baixo são listeners de `secao`/`document`, que sobrevivem ao
        // re-render: registrar de novo os duplicaria mesmo quando os nós internos mudam.
        if (this._globaisLigados) {
          this.sincronizar();
          return;
        }
        this._globaisLigados = true;

        // Troca de largura ou de cor do produto da página. O evento só serve de gatilho —
        // a largura vem do id da variante (ver larguraDaVariante), não do payload.
        this.secao.addEventListener('variant:update', (e) => {
          const variante = e.detail && e.detail.resource;
          if (variante && typeof variante.price === 'number') this.precoOrigem = variante.price;
          this.sincronizar();
        });

        // O quiz grava .value nos hidden dele por JS, e atribuir .value não dispara evento
        // nem mexe no atributo (MutationObserver não veria). Então re-lê depois de qualquer
        // interação da seção, que é o que move o quiz.
        this.secao.addEventListener('click', () => this.agendarSync(), true);
        this.secao.addEventListener('input', () => this.agendarSync());
        this.secao.addEventListener('change', () => this.agendarSync());

        // Última garantia, antes de o submit existir.
        // Delegado em `document`, não `botao.addEventListener`: este bloco renderiza ANTES
        // do buy-buttons no template (templates/product.json), então no connectedCallback os
        // botões ainda não existem — o querySelectorAll que ficava aqui pegava zero elementos
        // e nunca chegou a rodar. A delegação também cobre a barra sticky, que só dá .click()
        // no botão real (assets/sticky-add-to-cart.js:168).
        document.addEventListener('click', (e) => {
          var alvo = e.target && e.target.closest && e.target.closest('[ref="addToCartButton"]');
          if (!alvo) return;
          this.sincronizar();
          this.trackCarrinho();
        }, true);

        this.sincronizar();
      }

      fechar(linha) {
        linha.aberto = false;
        linha.cor = '';
      }

      /**
       * Funil do combo no GA4. Carimba em todo evento o produto da página, a largura e o
       * braço do teste A/B (snippets/ab-test.liquid), para dar de ler "expandiu → escolheu
       * cor → foi pro carrinho" por braço e por medida.
       */
      track(nome, dados) {
        enviarEvento(window, nome, Object.assign({
          product_handle: this.dataset.handle || undefined,
          produto: this.tituloOrigem || undefined,
          largura: this.largura || undefined,
          ab_variant: variantAB(typeof document !== 'undefined' ? document.cookie : ''),
        }, dados));
      }

      agendarSync() {
        clearTimeout(this._t);
        this._t = setTimeout(() => this.sincronizar(), 0);
      }

      /** Properties já preenchidas para o produto da página. */
      lerPropriedades() {
        var out = {};
        if (!this.formEl) return out;
        // form.elements (e não querySelectorAll) porque os campos do quiz ficam FORA do
        // <form> e entram por form="..." — o mesmo caminho que este bloco usa.
        Array.from(this.formEl.elements).forEach(function (el) {
          if (!el.name || el.disabled) return;
          var m = el.name.match(/^properties\[(.+)\]$/);
          if (!m) return;
          if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
          var v = (el.value || '').trim();
          if (v) out[m[1]] = v;
        });
        return out;
      }

      quantidade() {
        var q = this.formEl && this.formEl.querySelector('[name="quantity"]');
        return Math.max(1, parseInt((q && q.value) || '1', 10) || 1);
      }

      sincronizar() {
        var idOrigem = this.formEl.querySelector('[name="id"]');
        var larguraAtual = larguraDaVariante(this.largurasOrigem, idOrigem && idOrigem.value);
        if (larguraAtual) this.largura = larguraAtual;

        var cm = cmDaLargura(this.largura);
        var origem = this.lerPropriedades();
        var quantidade = this.quantidade();
        var escolhidos = [];

        var livre = this._livre;
        this.linhas.forEach((linha) => {
          var preco = livre ? linha.precoLivre : linha.precos[this.largura];

          // Modo livre ainda calculando o preço do par: a linha fica, sem preço e sem entrar.
          if (livre && preco === undefined) {
            linha.el.hidden = false;
            this.pintar(linha, null, false);
            return;
          }

          // Sem variante nessa largura o produto não é vendável aqui: a linha some em vez
          // de virar um gatilho que abre uma caixa sem preço nem cor válida.
          if (!preco) {
            this.fechar(linha);
            linha.el.hidden = true;
            this.pintar(linha, null, false);
            return;
          }
          linha.el.hidden = false;

          // No modo livre não há variante pronta: ela nasce no servidor na hora da compra.
          var varianteId = livre ? null : varianteDoPar(linha.mapa, this.largura, linha.cor);
          var ativo = !!(linha.aberto && (livre ? linha.cor : varianteId));
          if (ativo) {
            escolhidos.push({
              linha: linha,
              preco: preco,
              varianteId: varianteId,
              livre: !!livre,
              cor: linha.cor,
              quantidade: quantidade,
              props: propriedadesDoPar(origem, {
                cm: livre ? Math.round(livre.larguraM * 100) : cm,
                fator: linha.fator,
                prega: linha.prega,
                espelhos: linha.espelhos,
              }),
            });
          }
          this.pintar(linha, preco, ativo);
        });

        this._escolhidos = escolhidos;
        this.escreverCampos(escolhidos);
        this.atualizarResumo(escolhidos);
        this.atualizarBotao(escolhidos.length + 1);
      }

      /** Estado visual de uma linha: sinal do gatilho, preços, chips e aviso. */
      pintar(linha, preco, ativo) {
        if (linha.gt) linha.gt.setAttribute('aria-expanded', linha.aberto ? 'true' : 'false');
        if (linha.sig) linha.sig.textContent = ativo ? '✓' : linha.aberto ? '−' : '+';
        linha.el.classList.toggle('is-on', ativo);
        if (linha.alvo) linha.alvo.hidden = !linha.aberto;
        if (linha.elMedida) linha.elMedida.textContent = this._livre
          ? this._livre.larguraM.toFixed(2).replace('.', ',') + 'm'
          : (this.largura || '');
        if (linha.elPreco) linha.elPreco.textContent = preco ? formatarBRL(preco) : '';
        if (linha.elGtPreco) linha.elGtPreco.textContent = preco ? formatarBRL(preco) : '';

        var pedindo = linha.aberto && !ativo;
        linha.chips.forEach(function (chip) {
          var sel = chip.getAttribute('data-cb-cor') === linha.cor && !!linha.cor;
          chip.classList.toggle('is-selected', sel);
          chip.setAttribute('aria-pressed', sel ? 'true' : 'false');
          chip.classList.toggle('is-asking', pedindo);
        });

        if (linha.elAviso) {
          linha.elAviso.textContent = ativo
            ? 'Vai junto no pedido: ' + linha.titulo + ' ' + linha.cor + '.'
            : 'Escolha a cor para incluir no pedido.';
        }
        if (linha.elRm) linha.elRm.hidden = !ativo;
      }

      /**
       * Campos hidden dos itens extras. Recria só quando a assinatura muda: `sincronizar`
       * roda a cada clique da seção e trocar o DOM à toa custaria caro no mobile.
       */
      /**
       * Intenção de compra com combo, no clique do botão de comprar (o submit é tarde
       * demais — ver o cabeçalho). Deduplicado pela assinatura do que está escolhido:
       * quando o quiz bloqueia a validação o cliente clica várias vezes, e sem isso um
       * único combo viraria quatro eventos.
       */
      trackCarrinho() {
        var escolhidos = this._escolhidos || [];
        if (!escolhidos.length) return;
        var assinatura = escolhidos.map(function (e) {
          return e.varianteId + 'x' + e.quantidade;
        }).join('|');
        if (assinatura === this._ultimoCarrinho) return;
        this._ultimoCarrinho = assinatura;
        var extra = escolhidos.reduce(function (soma, e) {
          return soma + e.preco * e.quantidade;
        }, 0);
        this.track('combo_no_carrinho', {
          itens: escolhidos.length,
          valor_extra: extra / 100,
          pares: escolhidos.map(function (e) { return e.linha.titulo; }).join(', '),
        });
      }

      escreverCampos(escolhidos) {
        if (!this.elCampos) return;
        var campos = camposDosItens(escolhidos);
        var assinatura = JSON.stringify(campos);
        if (assinatura === this._assinatura) return;
        this._assinatura = assinatura;

        this.elCampos.textContent = '';
        var formId = this.dataset.formId;
        campos.forEach((par) => {
          var input = document.createElement('input');
          input.type = 'hidden';
          input.name = par[0];
          input.value = par[1];
          input.setAttribute('form', formId);
          this.elCampos.appendChild(input);
        });
      }

      atualizarResumo(escolhidos) {
        if (!this.elResumo) return;
        this.elResumo.hidden = !escolhidos.length;
        this.elResumo.textContent = '';
        if (!escolhidos.length) return;

        var precoCortina = this._livre && this.precoOrigemLivre ? this.precoOrigemLivre : this.precoOrigem;
        var total = precoCortina;
        var criar = (rotulo, valor, classe) => {
          var div = document.createElement('div');
          div.className = 'cb__resumo-linha' + (classe ? ' ' + classe : '');
          var a = document.createElement('span');
          a.className = 'cb__resumo-lbl';
          a.textContent = rotulo;
          var b = document.createElement('span');
          b.textContent = formatarBRL(valor);
          div.appendChild(a);
          div.appendChild(b);
          this.elResumo.appendChild(div);
        };

        criar(this.tituloOrigem, precoCortina);
        escolhidos.forEach(function (item) {
          total += item.preco;
        });
        escolhidos.forEach((item) => {
          criar(item.linha.titulo + ' · ' + item.linha.cor, item.preco);
        });
        criar('Subtotal', total, 'cb__resumo-tot');

        var promo = document.createElement('p');
        promo.className = 'cb__resumo-promo';
        promo.textContent = textoPromo(Date.now());
        this.elResumo.appendChild(promo);
      }

      /**
       * Modo livre (Cortina da Letícia fora da grade, 21/10/2026). Acima de 6,00 m de largura ou
       * de 3,30 m de altura a cortina é vendida pelo servidor das Medidas Livres e comprada pelo
       * botão da Letícia (assets/leticia-opcoes.js, comprarLivre) — o botão nativo, onde este bloco
       * se pendura, nem aparece. O par vai pelo mesmo caminho: preço de /api/price-livre na medida
       * real (o catálogo do par para em 6,00 m e mostraria o preço de outra largura) e variante
       * criada na compra, que pede `itensLivre()`. `null` volta ao normal.
       * @param {{larguraM: number, alturaM: number, precoCortina?: number}|null} medida
       */
      modoLivre(medida) {
        if (!this.linhas) return;
        if (medida && typeof medida.precoCortina === 'number') this.precoOrigemLivre = medida.precoCortina;
        var chave = medida ? medida.larguraM + 'x' + medida.alturaM : '';
        if (chave === this._chaveLivre) { this.sincronizar(); return; }
        this._chaveLivre = chave;
        this._livre = medida || null;
        this.linhas.forEach(function (linha) { linha.precoLivre = undefined; });
        if (medida) {
          var q = document.querySelector('quiz-personalizacao');
          var api = (q && q.dataset.api) || 'https://backend-promacal.vercel.app';
          this.linhas.forEach((linha) => {
            var cor = linha.chips[0] && linha.chips[0].getAttribute('data-cb-cor');
            if (!linha.modeloLivre || !cor) { linha.precoLivre = null; return; }
            fetch(api + '/api/price-livre', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ modelo: linha.modeloLivre, largura: medida.larguraM, altura: medida.alturaM, cor: cor }),
            }).then(function (r) { return r.json(); }).then((j) => {
              if (chave !== this._chaveLivre) return;
              linha.precoLivre = j && j.sucesso && !j.redirect && typeof j.precoFinal === 'number'
                ? Math.round(j.precoFinal * 100) : null;
              this.sincronizar();
            }).catch(() => {
              if (chave !== this._chaveLivre) return;
              linha.precoLivre = null;
              this.sincronizar();
            });
          });
        }
        this.sincronizar();
      }

      /** Pares escolhidos no modo livre: { linha, cor, preco, props } — a variante nasce na compra. */
      itensLivre() {
        if (!this._livre) return [];
        return (this._escolhidos || []).filter(function (e) { return e.livre; });
      }

      /**
       * Rótulo do botão. Usa só o texto que o Liquid mandou — NUNCA lê o texto atual do DOM:
       * o tema troca esse texto sozinho durante a animação de "adicionado" e re-renderiza a
       * seção, e ler dali já deixou o botão escrito "1".
       * Também não encosta no botão enquanto nada tiver sido escolhido alguma vez, para não
       * sobrescrever estados do tema como "Esgotado".
       */
      atualizarBotao(total) {
        var rotuloPadrao = this.dataset.rotuloPadrao;
        if (!rotuloPadrao) return;
        var ativo = total > 1;
        if (!ativo && !this._rotuloTrocado) return;
        this._rotuloTrocado = ativo;
        var texto = rotuloBotao(total, rotuloPadrao);
        document.querySelectorAll('[ref="addToCartButton"] .add-to-cart-text__content span span').forEach(
          function (el) {
            el.textContent = texto;
          }
        );
      }
    }

    if (!customElements.get('combo-blackout')) customElements.define('combo-blackout', ComboBlackout);
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof root !== 'undefined') root.PromacalComboBlackout = api;
})(typeof globalThis !== 'undefined' ? globalThis : this);
